import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from './client'
import { teamsApi } from './teams'
import { queryKeys } from './query-keys'
import type {
  ApiTeam,
  ApiTeamMemberInvite,
  InviteMemberRequest,
  UpdateTeamMemberRoleRequest,
} from './types'
import type { Store } from '@/lib/types/database'
import { useAuth } from '@/lib/auth/auth-context'

/** Map an ApiTeam from the backend to the Store shape the frontend expects */
export function teamToStore(team: ApiTeam): Store {
  const orgType = team.type === 'Bodyshop' ? 'body_shop'
    : team.type === 'DealershipGroup' ? 'group'
    : 'dealership'

  return {
    id: String(team.id),
    name: team.name,
    organization_type: orgType,
    parent_group_id: null,
    address: team.street ?? '',
    city: team.city ?? '',
    state: team.state ?? '',
    zip: team.zipCode ?? '',
    phone: team.phoneNumber ?? '',
    labor_rate: (team.metadata as any)?.laborRate?.bodyLabor ?? 0,
    created_at: team.createdAt,
    updated_at: team.updatedAt,
  }
}

async function fetchTeamsAsStores(): Promise<Store[]> {
  const teams = await teamsApi.list()
  return teams.map(teamToStore)
}

export interface TeamsData {
  all: Store[]
  groups: Store[]
  dealerships: Store[]
  bodyShops: Store[]
}

/** Fetch the user's teams, categorized by org type. Shared via query cache. */
export function useMyTeams() {
  const { user } = useAuth()

  return useQuery({
    queryKey: queryKeys.teams.list(),
    queryFn: async (): Promise<TeamsData> => {
      const stores = await fetchTeamsAsStores()
      return {
        all: stores,
        groups: stores.filter(s => s.organization_type === 'group'),
        dealerships: stores.filter(s => s.organization_type === 'dealership'),
        bodyShops: stores.filter(s => s.organization_type === 'body_shop'),
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch the bodyshop connected to a dealership. Returns null when none is connected
 * (backend responds 404). A dealership has at most one connected bodyshop.
 */
export function useConnectedBodyshop(dealershipId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.connectedBodyshop(dealershipId ?? 'none'),
    queryFn: async (): Promise<Store | null> => {
      try {
        const team = await teamsApi.getConnectedBodyshop(Number(dealershipId))
        return teamToStore(team)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    enabled: !!dealershipId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Fetch a single team by id. Returns the raw `ApiTeam` (not mapped to Store). */
export function useTeam(teamId: string | number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.detail(teamId ?? 'none'),
    queryFn: () => teamsApi.get(Number(teamId)),
    enabled: teamId != null && teamId !== '',
    staleTime: 5 * 60 * 1000,
  })
}

/** Fetch child dealerships for a group. */
export function useGroupDealerships(groupId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.dealerships(groupId!),
    queryFn: async (): Promise<Store[]> => {
      const teams = await teamsApi.listDealerships(Number(groupId))
      return teams.map(team => {
        const store = teamToStore(team)
        store.parent_group_id = String(groupId)
        return store
      })
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Invalidate all teams queries (call after creating a team, etc.) */
export function useInvalidateTeams() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: queryKeys.teams.all })
}

/**
 * Inverse of `useConnectedBodyshop` — list the dealerships connected to a bodyshop.
 * Hits the bodyshop-scoped path which returns `Team[]` directly (no group join).
 * 404 is treated as an empty list defensively.
 */
export function useDealershipsForBodyshop(bodyshopId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.connectedDealerships(bodyshopId ?? 'none'),
    queryFn: async (): Promise<Store[]> => {
      try {
        const teams = await teamsApi.listConnectedDealerships(Number(bodyshopId))
        return teams.map(teamToStore)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return []
        throw err
      }
    },
    enabled: !!bodyshopId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Disconnect the bodyshop currently linked to a dealership.
 * Real BE requires `{ bodyshopId }` in the DELETE body so a stale client can't
 * accidentally disconnect a bodyshop that was re-linked since the last GET.
 */
export function useDisconnectBodyshop(dealershipId: string | number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (bodyshopId: number) =>
      teamsApi.disconnectBodyshop(Number(dealershipId), { bodyshopId }),
    onSuccess: () => {
      if (dealershipId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.teams.connectedBodyshop(dealershipId) })
      }
      qc.invalidateQueries({ queryKey: queryKeys.teams.all })
    },
  })
}

/** Members of a team — slim summary for the Team Members UI. */
export function useTeamMembers(teamId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.teams.members(teamId ?? 'none'),
    queryFn: () => teamsApi.listMembers(Number(teamId)),
    enabled: !!teamId,
    staleTime: 30 * 1000,
  })
}

/** Pending invites for a team (owner-only on real BE; mock returns for any caller). */
export function useTeamInvites(teamId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.invites.byTeam(teamId ?? 'none'),
    queryFn: () => teamsApi.listInvites(Number(teamId)),
    enabled: !!teamId,
    staleTime: 30 * 1000,
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status
      if (status === 404 || status === 403) return false
      return failureCount < 2
    },
  })
}

/** Send an invite to a user. */
export function useInviteMember(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: InviteMemberRequest): Promise<ApiTeamMemberInvite> =>
      teamsApi.invite(Number(teamId), data),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.invites.byTeam(teamId) })
      }
      qc.invalidateQueries({ queryKey: queryKeys.teams.all })
    },
  })
}

/** Revoke a pending invite. */
export function useRevokeInvite(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => teamsApi.revokeInvite(Number(teamId), uuid),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.invites.byTeam(teamId) })
      }
    },
  })
}

/**
 * Re-send a pending invite email (POST /teams/:teamId/invites/:uuid/resend).
 * Backend gate: requester is a team member (membership only — owner role NOT
 * required, unlike revoke). 24h cooldown returns 400 with a user-safe message;
 * we surface that message via the existing ApiError toast pattern.
 */
export function useResendInvite(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string): Promise<ApiTeamMemberInvite> =>
      teamsApi.resendInvite(Number(teamId), uuid),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.invites.byTeam(teamId) })
      }
    },
  })
}

/** Remove a member from a team (DELETE /teams/:teamId/members/:userId). */
export function useRemoveMember(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => teamsApi.removeMember(Number(teamId), userId),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.teams.members(teamId) })
      }
    },
  })
}

/** Change a member's role on a team (PATCH /teams/:teamId/members/:userId). */
export function useUpdateMemberRole(teamId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: UpdateTeamMemberRoleRequest }) =>
      teamsApi.updateMemberRole(Number(teamId), userId, data),
    onSuccess: () => {
      if (teamId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.teams.members(teamId) })
      }
    },
  })
}

/**
 * Delete a dealership from a group (DELETE /teams/:groupId/group/dealerships/:dealershipId).
 * Invalidates the parent group's dealership list and the global teams cache
 * on success so the org chart and team switcher both refresh.
 */
export function useDeleteDealership(groupId: number | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dealershipId: number) =>
      teamsApi.deleteDealership(Number(groupId), dealershipId),
    onSuccess: () => {
      if (groupId != null) {
        qc.invalidateQueries({ queryKey: queryKeys.teams.dealerships(groupId) })
      }
      qc.invalidateQueries({ queryKey: queryKeys.teams.all })
    },
  })
}
